"use strict";

import onInitModulesInsScope from "./onInitModulesInScope";

/**
 * Ticket-Filter — presentational chrome for the ajax-form-powered ticket grid.
 *
 * Responsibilities:
 *  - Desktop dropdown: open/close per-group panels, outside-click + Esc
 *  - Mobile bottom sheet: open via mobile trigger or FAB, close via backdrop / close / Esc
 *  - Active chips: rebuild on form change (labels + remove buttons)
 *  - Count badges: per-group trigger, mobile trigger, FAB, sheet-apply (all .js-ticket-filter__total-count)
 *  - Info-panel toggle on each ticket card (re-bound after ajax swaps)
 *  - FAB visibility via IntersectionObserver on the filter section
 *
 * ajax-form (already initialised in content.js with submitOnChange)
 * handles all actual filtering/submission.
 */
export function init() {
    onInitModulesInsScope(function initInScope($scope) {

        // ── TICKET CARD INFO TOGGLE ─────────────────────────────────
        // Re-bound every time ajax-form replaces the result container.
        $scope.find('.js-ticket-card__info-toggle').on('click', function () {
            var $card = $(this).closest('.ticket-card');
            var isOpen = $card.toggleClass('is-info-open').hasClass('is-info-open');
            $(this).attr('aria-expanded', isOpen ? 'true' : 'false');
        });

        // ── FILTER SECTION SETUP ────────────────────────────────────
        // Filter UI lives OUTSIDE .js-ajax-form__result so it's only
        // initialised once (on the initial full-page scope).
        var $section = $scope.find('.js-ticket-filter');
        if (!$section.length) { return; }

        var $form        = $section.find('.js-ticket-filter__form');
        var $groups      = $section.find('.js-ticket-filter__group');
        var $activeWrap  = $section.find('.js-ticket-filter__active');
        var $totalCounts = $section.find('.js-ticket-filter__total-count');
        var $backdrop    = $section.find('.js-ticket-filter__backdrop');
        // FAB is a sibling of the section (outside), look in the broader scope
        var $fab         = $scope.find('.js-ticket-filter__fab');

        // ── DESKTOP: DROPDOWN OPEN / CLOSE ──────────────────────────
        $groups.each(function () {
            var $group   = $(this);
            var $trigger = $group.find('.js-ticket-filter__trigger');

            $trigger.on('click', function (e) {
                e.stopPropagation();
                var isOpen = $group.hasClass('is-open');

                // Close all groups
                $groups.removeClass('is-open');
                $groups.find('.js-ticket-filter__trigger').attr('aria-expanded', 'false');

                if (!isOpen) {
                    $group.addClass('is-open');
                    $trigger.attr('aria-expanded', 'true');
                }
            });
        });

        // Close dropdowns on outside click or Esc
        $(document).on('click.ticket-filter', function (e) {
            if (!$(e.target).closest('.js-ticket-filter__group').length) {
                $groups.removeClass('is-open');
                $groups.find('.js-ticket-filter__trigger').attr('aria-expanded', 'false');
            }
        });

        $(document).on('keydown.ticket-filter', function (e) {
            if (e.key === 'Escape') {
                $groups.removeClass('is-open');
                $groups.find('.js-ticket-filter__trigger').attr('aria-expanded', 'false');
                dismissSheet();
            }
        });

        // ── MOBILE SHEET: OPEN / CLOSE ──────────────────────────────

        // Snapshot of the last *applied* checkbox state.
        // Each entry is "name=value" for a checked box.
        var appliedState = [];

        function snapshotApplied() {
            appliedState = $form.find('input[type="checkbox"]:checked')
                .map(function () { return this.name + '=' + this.value; }).get();
        }

        // Restore checkboxes to the last snapshot WITHOUT triggering change events
        // (so no ajax request is fired).
        function restoreApplied() {
            $form.find('input[type="checkbox"]').each(function () {
                this.checked = appliedState.indexOf(this.name + '=' + this.value) !== -1;
            });
            renderActiveChips();
            updateCounts();
        }

        function openSheet() {
            snapshotApplied(); // capture current applied state before user starts ticking
            $section.addClass('is-sheet-open');
            $('body').addClass('sheet-open');
        }

        function closeSheet() {
            $section.removeClass('is-sheet-open');
            $('body').removeClass('sheet-open');
        }

        // Dismiss: revert unapplied changes, then close (no ajax)
        function dismissSheet() {
            restoreApplied();
            closeSheet();
        }

        // Open: mobile trigger (inside section) + FAB (outside section)
        $section.on('click', '.js-ticket-filter__open', openSheet);
        $fab.on('click', openSheet);

        // Close (X button) + backdrop = dismiss (revert + close)
        $section.on('click', '.js-ticket-filter__close', dismissSheet);
        $backdrop.on('click', dismissSheet);

        // Apply button ("Jetzt filtern"): submit, commit snapshot, then close
        $section.on('click', '.js-ticket-filter__apply', function () {
            var api = $section.closest('.js-ajax-form').data('ajax-form');
            if (api) { api.submit(); }
            snapshotApplied();
            closeSheet();
        });

        // ── FAB: SHOW/HIDE VIA INTERSECTION OBSERVER ────────────────
        // Watch the inline intro block (in normal flow on mobile) — not the
        // sheet's panel-outer which is off-screen (position: fixed) on mobile.
        var $intro = $section.find('.js-ticket-filter__intro');
        if ($intro.length && 'IntersectionObserver' in window) {
            var fabObserver = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    $fab.toggleClass('ticket-filter__fab--visible', !entry.isIntersecting);
                });
            }, { rootMargin: '-56px 0px 0px 0px', threshold: 0 });
            fabObserver.observe($intro[0]);
        }

        // ── ACTIVE CHIPS + COUNTS ────────────────────────────────────
        // Rebuild whenever any checkbox in the form changes.
        // On mobile (sheet open) suppress ajax-form's debounced auto-submit so
        // the request is deferred until the user taps "Jetzt filtern".
        $form.on('change', 'input[type="checkbox"]', function (e) {
            renderActiveChips();
            updateCounts();
            if ($section.hasClass('is-sheet-open')) {
                e.stopImmediatePropagation(); // block ajax-form's change handler
            }
        });

        function getCheckedByGroup() {
            var result = {};
            $groups.each(function () {
                var $g   = $(this);
                var key  = $g.data('filter');
                var vals = [];
                $g.find('input[type="checkbox"]:checked').each(function () {
                    var optionLabel = $(this).closest('.ticket-filter__option')
                        .find('.ticket-filter__option-label').text().trim();
                    vals.push({ value: $(this).val(), label: optionLabel || $(this).val() });
                });
                result[key] = { label: $g.find('.ticket-filter__trigger-label').text().trim(), values: vals };
            });
            return result;
        }

        function renderActiveChips() {
            $activeWrap.empty();

            var byGroup  = getCheckedByGroup();
            var total    = 0;
            var $chips   = [];

            Object.keys(byGroup).forEach(function (key) {
                var grp = byGroup[key];
                total += grp.values.length;

                grp.values.forEach(function (opt) {
                    var $chip = $('<span class="active-filter-chip"></span>').append(
                        $('<span></span>').html(grp.label + ': <strong>' + opt.label + '</strong>'),
                        $('<button type="button" class="active-filter-chip-remove" aria-label="Filter \'' + opt.label + '\' entfernen">' +
                            '<span class="icon icon-close" aria-hidden="true"></span></button>')
                    );

                    // Remove button unchecks the corresponding input → form change → ajax-form re-submits
                    $chip.find('.active-filter-chip-remove').on('click', function () {
                        var $checkbox = $form.find('input[type="checkbox"][name="' + key + '[]"][value="' + opt.value + '"]');
                        $checkbox.prop('checked', false).trigger('change');
                    });

                    $chips.push($chip);
                });
            });

            // Reset-all chip when 2+ filters active
            if (total >= 2) {
                var $resetChip = $(
                    '<button type="button" class="active-filter-chip active-filter-chip--reset">' +
                    '<span>Alle Filter zurücksetzen</span>' +
                    '<span class="icon icon-close" aria-hidden="true"></span></button>'
                );
                $resetChip.on('click', function () {
                    $section.find('.js-ajax-form__reset').first().trigger('click');
                });
                $chips.push($resetChip);
            }

            // Clone chips into every active-wrap container (intro on mobile + sheet on desktop)
            $activeWrap.each(function () {
                var $wrap = $(this);
                $chips.forEach(function ($chip) {
                    $wrap.append($chip.clone(true));
                });
            });
        }

        function updateCounts() {
            var byGroup = getCheckedByGroup();
            var total   = 0;

            // Per-group trigger count badges
            $groups.each(function () {
                var $g      = $(this);
                var key     = $g.data('filter');
                var count   = byGroup[key] ? byGroup[key].values.length : 0;
                var $badge  = $g.find('.js-ticket-filter__group-count');
                var $trigger = $g.find('.js-ticket-filter__trigger');

                total += count;

                if (count > 0) {
                    $badge.text(count).removeAttr('hidden');
                    $trigger.addClass('ticket-filter__trigger--has-value');
                } else {
                    $badge.attr('hidden', true);
                    $trigger.removeClass('ticket-filter__trigger--has-value');
                }
            });

            // All total-count badges (mobile trigger, sheet apply — inside section)
            // + FAB count (outside section, addressed separately)
            var $fabCount = $fab.find('.js-ticket-filter__total-count');
            if (total > 0) {
                $totalCounts.text(total).removeAttr('hidden');
                $fabCount.text(total).removeAttr('hidden');
                $section.find('.ticket-filter__reset--desktop').removeAttr('hidden');
            } else {
                $totalCounts.attr('hidden', true);
                $fabCount.attr('hidden', true);
                $section.find('.ticket-filter__reset--desktop').attr('hidden', true);
            }
        }

        // Run once on init (in case URL params pre-checked boxes)
        renderActiveChips();
        updateCounts();
        snapshotApplied(); // baseline for the first sheet open
    });
}
